import { useQueryClient } from '@tanstack/react-query';
import { Form, Formik, FormikHelpers } from 'formik';
import { useMemo } from 'react';
import { RxUpdate } from 'react-icons/rx';
import { TbSquareRoundedPlusFilled } from 'react-icons/tb';
import { toast } from 'react-toastify';
import {
  PAYMENT_METHOD_OPTIONS,
  SELECT_FIELD_DEFAULT_VALUE,
} from '../../../constants/settings';
import { useAuthContext } from '../../../contexts/AuthProvider';
import useBillingActions from '../../../hooks/APIHooks/finance/useBillingActions';
import useAssociateActions from '../../../hooks/APIHooks/users/useAssociateActions';
import { BillingHistory, BillingHistoryReqBody } from '../../../types/history';
import { showFullName } from '../../../utils/commonFunctions';
import { fetchErrorMessage } from '../../../utils/fetchErrorMessage';
import AcademiaButton from '../Buttons/AcademiaButton';
import AcademiaFormSelect from '../Inputs/AcademiaFormSelect';
import AcademiaInputField from '../Inputs/AcademiaInputField';
import AcademiaTextAreaField from '../Inputs/AcademiaTextAreaField';
import ModalTitle from './ModalTitle';
import ParentModal from './ParentModal';

const CreateEditBillingInfoModal = ({
  title,
  billingHistory,
  handleModalClose,
}: {
  title: 'create' | 'update';
  billingHistory?: BillingHistory;
  handleModalClose: () => void;
}) => {
  // integration of context hooks here
  const { currentUser } = useAuthContext();

  // integration of custom API hooks here
  const { associateListHandler } = useAssociateActions('associateList');
  const { createBillingInfoHandler, updateBillingInfoHandler } =
    useBillingActions('');

  // integration of tanstack query hooks here
  const queryClient = useQueryClient();

  const associateOptions = useMemo(() => {
    if (!associateListHandler.data?.data) return [];

    return associateListHandler.data.data.map((associate) => ({
      id: associate.id,
      name: showFullName(
        associate.first_name,
        associate.middle_name,
        associate.last_name
      ),
    }));
  }, [associateListHandler.data]);

  // handler function to handle create/edit billing history feature declared here
  const createEditBillHandler = async (
    values: any,
    actions: FormikHelpers<any>
  ) => {
    try {
      actions.setSubmitting(true);

      let body: BillingHistoryReqBody = {
        associate: values.associate.id,
        payment_method: values.payment_method.name,
        payment_info: values.payment_info,
        bank_account_number: values.bank_account_number,
        routing_number: values.routing_number,
        tax_id: values.tax_id,
      };

      if (!billingHistory) {
        body = { ...body, created_by: currentUser?.username };

        console.log('CREATE BILL BODY: ', body);

        await createBillingInfoHandler
          .mutateAsync({ body })
          .then((res) => {
            if (res.is_success) {
              toast.success('Bill Created!!');

              handleModalClose();
              queryClient.invalidateQueries({
                queryKey: ['billing-info-list'],
              });
            } else {
              toast.error(res.message || 'Bill Creation Failed!!');
            }
          })
          .catch((error) => toast.error(fetchErrorMessage(error)));
      } else {
        body = { ...body, updated_by: currentUser?.username };

        console.log('UPDATE BILL BODY: ', body);

        await updateBillingInfoHandler
          .mutateAsync({ billId: billingHistory.id, body })
          .then((res) => {
            if (res.is_success) {
              toast.success('Bill Updated!!');

              handleModalClose();
              queryClient.invalidateQueries({
                queryKey: ['billing-info-list'],
              });
            } else {
              toast.error(res.message || 'Bill Updating Failed!!');
            }
          })
          .catch((error) => toast.error(fetchErrorMessage(error)));
      }
    } catch (error) {
      toast.error('Something Went Wrong!!');
    } finally {
      actions.setSubmitting(false);
    }
  };

  // rendering create edit billing info modal component here
  return (
    <ParentModal onClick={handleModalClose}>
      <ModalTitle titleText={`${title} Bill`} />

      <Formik
        initialValues={{
          associate: billingHistory
            ? {
                id: billingHistory.associate,
                name: billingHistory.associate_full_name,
              }
            : SELECT_FIELD_DEFAULT_VALUE,
          payment_method: billingHistory
            ? PAYMENT_METHOD_OPTIONS.find(
                (method) => method.name === billingHistory.payment_method
              ) || SELECT_FIELD_DEFAULT_VALUE
            : SELECT_FIELD_DEFAULT_VALUE,
          bank_account_number: billingHistory?.bank_account_number || '',
          routing_number: billingHistory?.routing_number || '',
          tax_id: billingHistory?.tax_id || '',
          payment_info: billingHistory?.payment_info || '',
        }}
        onSubmit={createEditBillHandler}
      >
        {(props) => (
          <Form className='space-y-5'>
            <AcademiaFormSelect
              name='associate'
              label='Select Associate'
              options={associateOptions}
              defaultValue={props.values.associate}
              showSearchInput
              isRequired
            />

            <AcademiaFormSelect
              name='payment_method'
              label='Select Payment Method'
              options={PAYMENT_METHOD_OPTIONS}
              defaultValue={props.values.payment_method}
              showSearchInput
              isRequired
            />

            <AcademiaInputField
              type='text'
              name='bank_account_number'
              label='Bank Account Number'
              placeholder='Enter bank account number here...'
            />

            <AcademiaInputField
              type='text'
              name='routing_number'
              label='Routing Number'
              placeholder='Enter routing number here...'
            />

            <AcademiaInputField
              type='text'
              name='tax_id'
              label='Tax ID'
              placeholder='Enter tax id here...'
            />

            <AcademiaTextAreaField
              name='payment_info'
              label='Payment Info'
              placeholder='Enter additional payment info here...'
              isRequired
            />

            <div className='!mt-6'>
              <AcademiaButton
                title={
                  props.values.associate.id === -1
                    ? 'Please, select an associate first!'
                    : props.values.payment_method.id === -1
                    ? 'Please, select a payment method first!'
                    : undefined
                }
                type='submit'
                variant='premium'
                icon={
                  title === 'create' ? (
                    <TbSquareRoundedPlusFilled className='size-6' />
                  ) : (
                    <RxUpdate className='size-6' />
                  )
                }
                isLoading={props.isSubmitting}
                isDisabled={
                  props.values.associate.id === -1 ||
                  props.values.payment_method.id === -1
                }
                fullWidth
              >
                {title} Bill
              </AcademiaButton>
            </div>
          </Form>
        )}
      </Formik>
    </ParentModal>
  );
};

export default CreateEditBillingInfoModal;
